/**
 * AgeGateModal — Phase 2b Verifiable Parental Consent (VPC) entry point.
 *
 * Shown right after first sign-in. Asks the learner for their date of
 * birth.
 *
 *   ≥18  → recorded server-side, modal closes, full feature access.
 *   <18  → recorded server-side, learner can keep using the platform
 *          for free reading, but server-side guards block "Agentic"
 *          features (path generation, doubt solver, exam grading)
 *          until a parent/guardian completes verification at
 *          /api/parental-consent/* (DigiLocker / ID.me / email
 *          confirmation — currently parked, returns 501 until a
 *          partner ID is set).
 *
 * Industry standard (option ii from the consult): no hard signup
 * block, but reads + premium pieces are gated with a clean
 * "Parental approval needed" prompt so teens aren't kicked off
 * the platform.
 */
import { useEffect, useState } from "react";
import { Calendar, ShieldCheck, Mail, ExternalLink, Shield } from "lucide-react";
import { api } from "@/lib/api";
import VerifyParentalConsentModal from "@/components/VerifyParentalConsentModal";

const STORAGE_KEY = "oak_age_gate_v1";

function read() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}
function save(v) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {
    /* ignore */
  }
}

/** Years between two ISO dates in days, integer. */
function ageYears(dobIso) {
  const dob = new Date(dobIso);
  if (Number.isNaN(+dob)) return null;
  const ms = Date.now() - dob.getTime();
  return Math.floor(ms / (365.25 * 24 * 3600 * 1000));
}

export default function AgeGateModal({ user, onResolved }) {
  const [open, setOpen] = useState(false);
  const [dob, setDob] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [stage, setStage] = useState("ask"); // ask | minor | submitted
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Iter76 — Verifiable Parental Consent (VPC) instant verification modal.
  const [vpcOpen, setVpcOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const stored = read();
    // Fast-path: localStorage cached "resolved" from a previous session
    // on the same device. Skip the network hit entirely.
    if (stored?.user_id === user.user_id && stored?.resolved) {
      onResolved?.(stored);
      return undefined;
    }
    // Slow-path: hit the server. On a NEW browser / device / after a
    // localStorage wipe, the DOB is still recorded server-side in
    // user_prefs — so we should NOT re-ask. Earlier versions skipped
    // this check and re-prompted on every login from a fresh device,
    // which was a regression reported on 16 May 2026.
    (async () => {
      try {
        const { data } = await api.get("/age-gate/status");
        if (cancelled) return;
        if (data?.recorded) {
          const synced = {
            user_id: user.user_id,
            resolved: true,
            minor: !!data.is_minor,
            parental_consent: !!data.parental_consent,
            recorded_at: data.age_recorded_at,
          };
          save(synced);
          onResolved?.(synced);
          return;
        }
        // No record on the server → ask for DOB.
        setOpen(true);
      } catch {
        // Network/auth blip — fall back to asking. Worst case: the
        // user re-enters DOB and we upsert harmlessly.
        if (!cancelled) setOpen(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user, onResolved]);

  if (!open || !user) return null;

  const submitDob = async () => {
    setError(null);
    if (!dob) return setError("Please enter your date of birth");
    const yrs = ageYears(dob);
    if (yrs === null || yrs < 5 || yrs > 120) {
      return setError("That doesn't look right — please re-enter");
    }
    setSubmitting(true);
    try {
      await api.post("/age-gate/record", { dob, age: yrs });
      if (yrs < 18) {
        setStage("minor");
      } else {
        save({ user_id: user.user_id, resolved: true, dob, age: yrs, minor: false });
        onResolved?.({ minor: false, age: yrs });
        setOpen(false);
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        // Backend route missing — almost always means a stale deploy.
        // Don't block the user; persist locally so they can keep
        // working, surface a friendly note.
        save({ user_id: user.user_id, resolved: true, dob, age: yrs, minor: yrs < 18, status: "deferred" });
        onResolved?.({ minor: yrs < 18, age: yrs, deferred: true });
        setOpen(false);
        return;
      }
      setError(e?.response?.data?.detail || "Could not save — try again");
    } finally {
      setSubmitting(false);
    }
  };

  const requestConsent = async () => {
    setError(null);
    if (!parentEmail.includes("@")) return setError("Please enter a valid parent/guardian email");
    setSubmitting(true);
    try {
      await api.post("/parental-consent/request", { parent_email: parentEmail, dob });
      save({ user_id: user.user_id, resolved: true, dob, age: ageYears(dob), minor: true, parent_email: parentEmail, status: "pending" });
      setStage("submitted");
      onResolved?.({ minor: true, age: ageYears(dob), pending: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not send request — try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center p-4 bg-black/70 backdrop-blur-sm" data-testid="age-gate-root">
      <div
        role="dialog"
        aria-label="Age verification"
        className="max-w-md w-full rounded-2xl border border-white/12 bg-[#0B0E14]/95 shadow-2xl p-6 space-y-4"
        data-testid="age-gate-modal"
      >
        <div className="label-eyebrow !text-[10px] tracking-[0.25em] text-[#00FF9D]/85 flex items-center gap-1.5">
          <ShieldCheck size={11} /> Quick safety check
        </div>

        {stage === "ask" && (
          <>
            <h2 className="font-display text-xl tracking-tight">When were you born?</h2>
            <p className="text-white/55 text-[13px] leading-relaxed">
              We ask once so we can keep Oak AI Campus safe and DPDPA / GDPR / COPPA-compliant.
              Under-18 learners need a parent or guardian to approve advanced AI features — but
              you can still browse the public library while we wait.
            </p>
            <label className="block">
              <span className="text-[11px] text-white/55 tracking-wide flex items-center gap-1.5">
                <Calendar size={11} /> Date of birth
              </span>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#00FF9D]/50 outline-none"
                data-testid="age-gate-dob-input"
              />
            </label>
            {error && <div className="text-rose-300 text-[12px]" data-testid="age-gate-error">{error}</div>}
            <button
              onClick={submitDob}
              disabled={submitting}
              className="w-full text-sm font-display py-2.5 rounded-lg bg-gradient-to-r from-[#0044FF] to-[#5D3FD3] text-white disabled:opacity-50"
              data-testid="age-gate-submit"
            >
              {submitting ? "Saving…" : "Continue"}
            </button>
          </>
        )}

        {stage === "minor" && (
          <>
            <h2 className="font-display text-xl tracking-tight">Almost there — let's loop in a parent</h2>
            <p className="text-white/55 text-[13px] leading-relaxed">
              Because you're under 18, we need a parent or guardian to approve advanced features
              (path generation, the Doubt Solver, exams). They'll receive a one-tap approval link.
              Reading the public library and saved courses stays open in the meantime.
            </p>
            <label className="block">
              <span className="text-[11px] text-white/55 tracking-wide flex items-center gap-1.5">
                <Mail size={11} /> Parent or guardian email
              </span>
              <input
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                placeholder="parent@example.com"
                className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#00FF9D]/50 outline-none"
                data-testid="age-gate-parent-email"
              />
            </label>
            <p className="text-[11px] text-white/40 leading-relaxed">
              Indian learners can also verify instantly via DigiLocker once we're listed there
              — that link will appear here automatically.
            </p>
            <button
              type="button"
              onClick={() => setVpcOpen(true)}
              className="w-full text-[12.5px] font-display py-2.5 rounded-lg border border-[#0044FF]/40 bg-[#0044FF]/8 hover:bg-[#0044FF]/16 text-[#9bc7ff] inline-flex items-center justify-center gap-2 transition"
              data-testid="age-gate-vpc-cta"
            >
              <Shield size={13} /> Verify instantly with DigiLocker / ID.me
            </button>
            {error && <div className="text-rose-300 text-[12px]">{error}</div>}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setOpen(false);
                  save({ user_id: user.user_id, resolved: true, dob, age: ageYears(dob), minor: true, status: "deferred" });
                  onResolved?.({ minor: true, age: ageYears(dob), deferred: true });
                }}
                className="text-[12px] px-3 py-2 rounded-lg border border-white/15 text-white/65 hover:text-white"
                data-testid="age-gate-skip"
              >
                Do later
              </button>
              <button
                onClick={requestConsent}
                disabled={submitting}
                className="flex-1 text-sm font-display py-2.5 rounded-lg bg-gradient-to-r from-[#0044FF] to-[#5D3FD3] text-white disabled:opacity-50"
                data-testid="age-gate-request-consent"
              >
                {submitting ? "Sending…" : "Email parent for approval"}
              </button>
            </div>
          </>
        )}

        {stage === "submitted" && (
          <>
            <h2 className="font-display text-xl tracking-tight flex items-center gap-2">
              <ShieldCheck size={18} className="text-[#00FF9D]" /> Approval request sent
            </h2>
            <p className="text-white/65 text-[13px] leading-relaxed">
              We've emailed <span className="text-white">{parentEmail}</span> a one-tap approval
              link. Advanced features unlock the moment they tap it. Until then, browse the
              <a href="/explore" className="text-[#8aa8ff] inline-flex items-center gap-1 ml-1">
                public library <ExternalLink size={11} />
              </a>
              .
            </p>
            <button
              onClick={() => setOpen(false)}
              className="w-full text-sm font-display py-2.5 rounded-lg bg-white/[0.06] border border-white/15 text-white/85"
              data-testid="age-gate-close"
            >
              Got it
            </button>
          </>
        )}
      </div>
      <VerifyParentalConsentModal
        open={vpcOpen}
        onOpenChange={setVpcOpen}
        onConfirmed={() => {
          // Persist locally so we don't re-prompt, then resolve.
          save({ user_id: user.user_id, resolved: true, dob, age: ageYears(dob), minor: true, parental_consent: true });
          onResolved?.({ minor: true, age: ageYears(dob), parental_consent: true });
          setOpen(false);
        }}
      />
    </div>
  );
}
